/* eslint-disable complexity */
/* eslint-disable no-magic-numbers */
import React, { useCallback } from 'react';

import { useIntl } from '@futrli/localisation';
import { checkFileExtension } from '@futrli/predict/report-builder/utils-generic';
import { toastError, UILoaderSpinner } from '@futrli/ui-components';
import { ExcelEntityConfig } from '@futrli/views/ExcelFilesView';

import { StepType as StepTypeOrg } from '../hooks/use-upload-organisation';
import { StepType as StepTypeForecast } from '../hooks/use-upload-forecast';
import { ExcelUploadDrawerForm } from './excel-upload-drawer-form';
import { UploadEntity } from './upload-entity';

interface Props {
  children: JSX.Element;
  maxFileSize?: number; // in MB
  acceptedFileExtensions?: string;
  onCancel: () => void;
  isInProgress: boolean;
  uploadSuccess: boolean;
  uploadFailed: boolean;
  file?: File;
  errorMessage: string;
  warningMessage?: string;
  step: StepTypeOrg | StepTypeForecast;
  entity: UploadEntity;
  onFileChange: (file: File) => void;
  onFinish: () => Promise<void>;
  isLoading?: boolean;
  onCreate?: () => void;
  isUpdateDrawer?: boolean;
  workspaceId?: string;
  organisationId?: string;
  historyConfig?: ExcelEntityConfig;
  disableCreate?: boolean;
  width?: number;
}

export const ExcelUploadDrawer = ({
  children,
  maxFileSize = 25,
  acceptedFileExtensions = '.xls,.xlsx',
  onCancel,
  isInProgress,
  uploadSuccess,
  uploadFailed,
  file,
  errorMessage,
  warningMessage,
  step,
  entity,
  onFileChange,
  onFinish,
  isLoading,
  onCreate,
  isUpdateDrawer,
  workspaceId,
  organisationId,
  historyConfig,
  disableCreate,
  width
}: Props) => {
  const intl = useIntl();

  const validateFile = useCallback(
    (file: File) => {
      if (!checkFileExtension(file.name, acceptedFileExtensions)) {
        toastError(intl.formatMessage({ id: 'predict.file_not_supported', defaultMessage: 'File not supported' }));
        return false;
      } else if (file.size > maxFileSize * 1024 * 1024) {
        toastError(intl.formatMessage({ id: 'predict.file_too_big', defaultMessage: 'File is too big' }));
        return false;
      }
      return true;
    },
    [acceptedFileExtensions, maxFileSize, intl]
  );

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (validateFile(acceptedFiles[0])) {
        onFileChange(acceptedFiles[0]);
      }
    },
    [onFileChange, validateFile]
  );

  return isLoading ? (
    <UILoaderSpinner />
  ) : (
    <ExcelUploadDrawerForm
      acceptedFileExtensions={acceptedFileExtensions}
      onCancel={onCancel}
      isInProgress={isInProgress}
      uploadSuccess={uploadSuccess}
      uploadFailed={uploadFailed}
      file={file}
      errorMessage={errorMessage}
      warningMessage={warningMessage}
      step={step}
      entity={entity}
      onFinish={onFinish}
      onCreate={onCreate}
      onDrop={onDrop}
      isUpdateDrawer={isUpdateDrawer}
      workspaceId={workspaceId}
      organisationId={organisationId}
      historyConfig={historyConfig}
      disableCreate={disableCreate}
      width={width}
    >
      {children}
    </ExcelUploadDrawerForm>
  );
};
